import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mealplan } from '../lib/api';
import RecipeThumbnail from '../components/RecipeThumbnail';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', icon: '🌅' },
  { id: 'lunch', label: 'Lunch', icon: '☀️' },
  { id: 'dinner', label: 'Dinner', icon: '🌙' },
];

function getWeekDates(baseDate) {
  const date = new Date(baseDate);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  monday.setHours(0, 0, 0, 0);

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

function formatWeekRange(weekStart, weekEnd) {
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const year = start.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}, ${year}`;
  }
  return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${year}`;
}

function RecipePickerModal({ isOpen, onClose, onSelect, search, setSearch, recipes, isLoading }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Recipe</h3>
            <button onClick={onClose} className="btn-icon">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search recipes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : recipes?.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {search ? 'No recipes found' : 'No recipes saved yet'}
            </div>
          ) : (
            <div className="space-y-2">
              {recipes?.map((recipe) => (
                <button
                  key={recipe.id}
                  onClick={() => onSelect(recipe)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <RecipeThumbnail recipe={recipe} size="small" showTitle={false} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{recipe.title}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      {recipe.totalTime && <span>{recipe.totalTime}</span>}
                      {recipe.servings && <span>{recipe.servings}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MealSlot({ meal, mealType, date, onAdd, onRemove, isToday }) {
  const mealInfo = MEAL_TYPES.find((m) => m.id === mealType);

  return (
    <div className={`p-2 rounded-lg border ${isToday ? 'border-primary-200 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'}`}>
      <div className="flex items-center gap-1 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        <span>{mealInfo?.icon}</span>
        <span>{mealInfo?.label}</span>
      </div>
      {meal?.recipe ? (
        <div className="relative group">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded flex-shrink-0 overflow-hidden">
              <RecipeThumbnail recipe={meal.recipe} size="small" showTitle={false} />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
              {meal.recipe.title}
            </p>
          </div>
          <button
            onClick={() => onRemove(meal.id)}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            title="Remove"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        <button
          onClick={() => onAdd(date, mealType)}
          className="w-full h-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded flex items-center justify-center text-gray-400 hover:border-primary-400 hover:text-primary-500 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}
    </div>
  );
}

function MealPlan() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentWeek, setCurrentWeek] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [recipeSearch, setRecipeSearch] = useState('');

  const weekDates = useMemo(() => getWeekDates(currentWeek), [currentWeek]);
  const todayStr = new Date().toISOString().split('T')[0];

  const { data: weekData, isLoading: isLoadingWeek } = useQuery({
    queryKey: ['mealplan', weekDates[0]],
    queryFn: () => mealplan.getWeek(weekDates[0]),
  });

  const { data: recipesData, isLoading: isLoadingRecipes } = useQuery({
    queryKey: ['mealplan-recipes', recipeSearch],
    queryFn: () => mealplan.getRecipes(recipeSearch),
    enabled: pickerOpen,
  });

  const addMealMutation = useMutation({
    mutationFn: ({ date, mealType, recipeId }) => mealplan.addMeal(date, mealType, recipeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealplan'] });
      setPickerOpen(false);
      setSelectedSlot(null);
    },
  });

  const removeMealMutation = useMutation({
    mutationFn: (mealPlanId) => mealplan.removeMeal(mealPlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealplan'] });
    },
  });

  const clearWeekMutation = useMutation({
    mutationFn: (weekDate) => mealplan.clearWeek(weekDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealplan'] });
    },
  });

  const navigateWeek = (direction) => {
    const current = new Date(weekDates[0]);
    current.setDate(current.getDate() + (direction * 7));
    setCurrentWeek(current.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setCurrentWeek(new Date().toISOString().split('T')[0]);
  };

  const handleAddClick = (date, mealType) => {
    setSelectedSlot({ date, mealType });
    setRecipeSearch('');
    setPickerOpen(true);
  };

  const handleRecipeSelect = (recipe) => {
    if (selectedSlot) {
      addMealMutation.mutate({
        date: selectedSlot.date,
        mealType: selectedSlot.mealType,
        recipeId: recipe.id,
      });
    }
  };

  const handleRemoveMeal = (mealPlanId) => {
    removeMealMutation.mutate(mealPlanId);
  };

  const handleClearWeek = () => {
    if (window.confirm('Are you sure you want to clear all meals for this week?')) {
      clearWeekMutation.mutate(weekDates[0]);
    }
  };

  const handleGenerateShoppingList = () => {
    navigate(`/shopping?fromMealPlan=true&week=${weekDates[0]}`);
  };

  const hasMeals = weekData?.mealPlans && Object.keys(weekData.mealPlans).length > 0;

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Meal Plan</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Plan your meals for the week
          </p>
        </div>
        {hasMeals && (
          <div className="flex gap-2">
            <button
              onClick={handleGenerateShoppingList}
              className="btn btn-primary"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
              </svg>
              Shopping List
            </button>
            <button
              onClick={handleClearWeek}
              disabled={clearWeekMutation.isPending}
              className="btn btn-secondary text-red-600 dark:text-red-400"
            >
              {clearWeekMutation.isPending ? (
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              )}
              Clear Week
            </button>
          </div>
        )}
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-6 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <button
          onClick={() => navigateWeek(-1)}
          className="btn-icon"
          title="Previous week"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {weekData ? formatWeekRange(weekData.weekStart, weekData.weekEnd) : 'Loading...'}
          </h2>
          <button
            onClick={goToToday}
            className="btn btn-secondary text-sm py-1 px-3"
          >
            Today
          </button>
        </div>

        <button
          onClick={() => navigateWeek(1)}
          className="btn-icon"
          title="Next week"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Week Grid */}
      {isLoadingWeek ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          {weekDates.map((date, index) => {
            const dayMeals = weekData?.mealPlans?.[date] || {};
            const isToday = date === todayStr;
            const dateObj = new Date(date);
            const dayNum = dateObj.getDate();
            const monthShort = dateObj.toLocaleDateString('en-US', { month: 'short' });

            return (
              <div
                key={date}
                className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm ${
                  isToday ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <div className={`text-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700`}>
                  <p className={`text-xs font-medium ${isToday ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {DAYS_OF_WEEK[index]}
                  </p>
                  <p className={`text-lg font-bold ${isToday ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                    {dayNum}
                  </p>
                  <p className="text-xs text-gray-400">{monthShort}</p>
                </div>

                <div className="space-y-2">
                  {MEAL_TYPES.map((mealType) => (
                    <MealSlot
                      key={mealType.id}
                      meal={dayMeals[mealType.id]}
                      mealType={mealType.id}
                      date={date}
                      onAdd={handleAddClick}
                      onRemove={handleRemoveMeal}
                      isToday={isToday}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recipe Picker Modal */}
      <RecipePickerModal
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setSelectedSlot(null);
        }}
        onSelect={handleRecipeSelect}
        search={recipeSearch}
        setSearch={setRecipeSearch}
        recipes={recipesData?.recipes}
        isLoading={isLoadingRecipes || addMealMutation.isPending}
      />
    </div>
  );
}

export default MealPlan;
